import React, { useState } from "react";
import styled from "styled-components";
import { IconButton, Typography } from "@mui/material";
import { useDropzone } from "react-dropzone";
import { Delete } from "@mui/icons-material";
import { StyledErrorMessage } from "../style/modalStyle";
import { StyledLink } from "../style/ititn/itinUpload";
import PassportInstructionModal from "../itin/PassportInstructionModal";

interface FilePropType {
  title?: string;
  icon?: React.ReactNode;
  desc?: string;
  name?: string;
  formik?: any;
  handleFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  filePreview: string | null;
  setFilePreview: React.Dispatch<React.SetStateAction<string | null>>;
  disabled?: boolean;
  accept?: string; 
  showInstruction?: boolean;
}
const data = [
  {
    instructions: [
      "All four corners of the full passport should be visible. Both the top half and bottom half of the passport should included in the picture.",
      "The photo should be in color and not blurry.",
      "The passport should be valid least 6 months and not expired.",
      "Please scan your passport with the scanner.",
    ],
  },
];
const UploadFileFieldSingleDoc = (props: FilePropType) => {
  const {
    title,
    icon,
    name,
    formik,
    handleFileChange,
    setFilePreview,
    filePreview,
    disabled,
    accept, 
    showInstruction,
  } = props;

  const fieldName = name || "file-upload";
  const error = formik?.touched[fieldName] && formik?.errors[fieldName];
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const parsedAccept = accept
    ? accept.split(",").reduce((acc: any, type) => {
        const trimmed = type.trim();
        if (!acc[trimmed]) acc[trimmed] = [];
        return acc;
      }, {})
    : {
        "image/jpeg": [".jpeg", ".jpg"],
        "image/png": [".png"],
        "application/pdf": [".pdf"],
        "application/msword": [".doc"],
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
          [".docx"],
      };


  const { getRootProps, getInputProps } = useDropzone({
    accept: parsedAccept,
    onDrop: (acceptedFiles, rejectedFiles) => {
      if (rejectedFiles.length > 0) {
        setUploadError("Invalid file type.");
        return;
      }

      if (acceptedFiles.length > 0) {
        const file = acceptedFiles[0];
        formik.setFieldValue(fieldName, [file]);
        setFilePreview(file.name);
        setUploadError(null); 
      }
    },
  });

  const removeFile = () => {
    formik.setFieldValue(fieldName, null);
    setFilePreview(null);
  };

  return (
    <>
      <Wrapper
        style={{
          border: error ? "1px solid red" : "1px solid transparent",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            border:  "1px solid transparent",
          }}
        >
          <Title variant="h4">{title}</Title>
          {showInstruction && (
            <StyledLink to="" onClick={() => setIsModalOpen(true)}>
              Passport Instruction
            </StyledLink>
          )}
        </div>
        <DropArea {...getRootProps()}>
          <input {...getInputProps()} disabled={disabled} />
          <UploadContainer>
            {icon}
            <StyledTypography>
              {title !== "Scan Passport" ? (
                <>
                  <span>Click here</span> or drag & drop your file
                </>
              ) : (
                "Scan your passport"
              )}
            </StyledTypography>
          </UploadContainer>
        </DropArea>

        {filePreview && (
          <PreviewContainer>
            <Typography
              sx={{
                overflowX: "auto",
              }}
            >
              {filePreview}
            </Typography>
            <IconButton onClick={removeFile} disabled={disabled}>
              <Delete color="error" />
            </IconButton>
          </PreviewContainer>
        )}
      </Wrapper>

      <StyledErrorMessage>{uploadError || error}</StyledErrorMessage>

      <PassportInstructionModal
        open={isModalOpen}
        handleClose={() => setIsModalOpen(false)}
        title="Passport Upload Instruction"
        data={data}
      />
    </>
  );
};

export default UploadFileFieldSingleDoc;

const Wrapper = styled.div`
  background: white;
  padding: 24px;
  border-radius: 10px;
  box-shadow: 0px 2px 40px 0px #dde3ea33;
`;
const Title = styled(Typography)`
  font-size: 16px !important;
  font-weight: 500 !important;
  line-height: 20px !important;
`;
const DropArea = styled.div`
  margin-top: 10px;
  border: 0.6px dashed #2977cc;
  border-radius: 10px;
  padding: 40px;
  background: #eaf1fa;
  text-align: center;
  cursor: pointer;
`;
const UploadContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  flex-direction: column;
  cursor: pointer;
`;
const StyledTypography = styled(Typography)`
  font-size: 14px;
  font-weight: 400;
  color: #2c3034;
  span {
    font-size: 14px;
    font-weight: 600;
    color: #2977cc;
  }
  overflow-x: auto;
`;
const PreviewContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 10px;
  margin-top: 10px;
  border-radius: 5px;
  background: #f5f5f5;
  .file-preview {
    max-width: 100px;
    max-height: 100px;
    border-radius: 5px;
  }
`;
